<template>
  <Loading />
  <n-config-provider :theme="darkTheme" :theme-overrides="themeOverrides">
    <n-loading-bar-provider>
      <n-message-provider :placement="messagePlacement">
        <n-notification-provider>
          <n-modal-provider>
            <n-dialog-provider>
              <ToastInjector />
              <Sidebar :menu-options="menuOptions" :logo-src="'/images/Logo-AgentSnap-400px.png'" title="AgentSnap">
                <n-layout id="default_layout_div" ref="DefaultLayoutDiv" :native-scrollbar="false">
                  <n-layout-header>
                    <Header />
                  </n-layout-header>
                  <n-layout-content :native-scrollbar="false">
                    <main class="sr__main-body" @click="collapseSidebar">
                      <div>
                        <CheckUserStatus />
                        <div class="d-flex welcome">
                          <div>
                            <h2 v-if="$auth?.businessName" class="text-white mb-1">
                              Welcome, {{ $auth?.businessName }}
                            </h2>
                            <h2 v-else class="text-white mb-1">
                              Welcome, {{ $auth?.firstName }} {{ $auth?.lastName }}
                            </h2>
                            <p>
                              <span>AgentSnap</span>
                              is fast, simple, and secure
                            </p>
                          </div>
                        </div>

                        <template v-if="$auth?.approved === false">
                          <div class="mt-4 text-left sr__not-approved">
                            <p>
                              Our team is reviewing your account for approval now. Hang in there, this shouldn't take
                              long.
                            </p>
                            <p>
                              Want to get approved faster? Email us at
                              <a :href="`mailto:support@agentsnap.io?subject=${supportMail}`" data-rel="external"
                                >Support@agentsnap.io</a
                              >
                            </p>
                          </div>
                        </template>

                        <div class="body-wrapper clearfix">
                          <div class="body-content">
                            <div class="row">
                              <slot />
                            </div>
                          </div>
                        </div>
                      </div>
                    </main>
                  </n-layout-content>
                  <n-layout-footer position="absolute" style="z-index: 990">
                    <div class="help-text">
                      <div>Need help? We're here <span style="color: red">❤</span>.</div>
                      <div>
                        Email us at
                        <a :href="`mailto:support@agentsnap.io?subject=${supportMail}`" data-rel="external"
                          >Support@agentsnap.io</a
                        >
                      </div>
                    </div>

                    <Idle :timeout="1800" style="display: none" />
                  </n-layout-footer>
                </n-layout>
              </Sidebar>
              <VerifyPhoneToast
                :show="isVerifyPhoneToastVisible"
                @hide-toast="hideToast"
                @verify-click="onVerifyClick"
              />
            </n-dialog-provider>
          </n-modal-provider>
        </n-notification-provider>
      </n-message-provider>
    </n-loading-bar-provider>
  </n-config-provider>
</template>

<script setup lang="ts">
import { NImage, darkTheme } from 'naive-ui';
import type { MenuOption, NLayout } from 'naive-ui';
import { Home, Settings } from '@vicons/tabler';
// SR-2023: WritingSign icon removed - was used for signatures menu item
import { RouterLink } from 'vue-router';
import { getGlobalThemeOverrides } from '../../base/utils/theme-overrides';
import { DwollaCustomerType } from '../../base/common/enums/dwolla-customer-type';
import { AccountAppType } from '../../base/common/enums/account-app-type';

const router = useRouter();
const route = useRoute();
const { data: $auth } = useAuth();
const profileStore = useProfileStore();
const { isVerifyPhoneToastVisible } = storeToRefs(profileStore);
const userRoleStore = useUserRolePermissionsStore();
const { permissions } = storeToRefs(userRoleStore);
const { sidebarCollapsedData: collapsed, setSidebarCollapsed } = useSidebarCollapse();

const { $sendButtonClickEvent, $sendEvent } = useNuxtApp();

let unsubscribeSendEventHook: () => void;

const supportMail = ref<string>('AgentSnap Support Request');
const DefaultLayoutDiv = ref<InstanceType<typeof NLayout> | null>(null);

const themeOverrides = getGlobalThemeOverrides();

const themeColors = computed(() => {
  return {
    primaryColor: null,
    secondaryColor: null,
  };
});

provide('themeColors', themeColors);

const isAgent = computed(() => {
  return $auth.value?.type === DwollaCustomerType.Business;
});

const hideToast = () => {
  profileStore.$patch({ isVerifyPhoneToastVisible: false });
};

const onVerifyClick = () => {
  navigateTo({ name: 'my-preference' });
  hideToast();
};

const menuOptions = computed<MenuOption[]>(() => [
  {
    show: isAgent.value && permissions.value.INVOICES_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'invoices',
          },
        },
        { default: () => `${$auth.value?.isStripeEnabled ? 'Invoices' : 'Dashboard'}` },
      ),
    key: 'invoices',
    icon: renderIcon(Home),
  },
  {
    show: !isAgent.value && permissions.value.PENDING_PAYMENTS_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'pending-payment',
          },
        },
        { default: () => 'Pending Payments' },
      ),
    key: 'pending-payment',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/open-reload.svg' })),
  },
  {
    show: !isAgent.value && permissions.value.TRANSACTION_HISTORY_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'transactions',
          },
        },
        { default: () => 'Transaction History' },
      ),
    key: 'transactions',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/ionic-ios-paper.svg' })),
  },
  {
    show: $auth.value?.isStripeEnabled && permissions.value.CATALOGS_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'catalogs',
          },
        },
        { default: () => 'Catalogs' },
      ),
    key: 'catalogs',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/ionic-ios-paper.svg' })),
  },
  // SR-2023: Temporarily disabled signature functionality
  // {
  //   show: isAgent.value && permissions.value.SIGNTAURES_PAGE,
  //   label: () =>
  //     h(
  //       RouterLink,
  //       {
  //         to: {
  //           name: 'signatures',
  //         },
  //       },
  //       { default: () => 'Signatures' },
  //     ),
  //   key: 'signatures',
  //   icon: renderIcon(WritingSign),
  // },
  {
    show: permissions.value.BANKS_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'bank',
          },
        },
        { default: () => 'Banks' },
      ),
    key: 'bank',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/ionic-ios-card.svg' })),
  },
  {
    show: permissions.value.PREFERENCES_PAGE,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'my-preference',
          },
        },
        { default: () => 'Preferences' },
      ),
    key: 'my-preference',
    icon: renderIcon(Settings),
  },
  {
    show: permissions.value.TEAMS_PAGE && $auth.value?.type != DwollaCustomerType.ReceiveOnly,
    label: () =>
      h(
        RouterLink,
        {
          to: {
            name: 'teams',
          },
        },
        { default: () => 'Teams' },
      ),
    key: 'teams',
    icon: renderIcon(h(NImage, { width: 24, src: '/images/teams.svg' })),
  },
]);

const collapseSidebar = () => {
  if (window.innerWidth < 768 && !collapsed.value) {
    setSidebarCollapsed(true);
  }
};

const userRolePermissionsStore = useUserRolePermissionsStore();

watchEffect(async () => {
  const accountType = $auth.value?.accountAppType as AccountAppType;
  if (
    (accountType === AccountAppType.AgentSnap || accountType === AccountAppType.DualApp) &&
    $auth.value?.type !== DwollaCustomerType.ReceiveOnly
  ) {
    if (!userRolePermissionsStore.role) {
      const userRoles = localStorage.getItem('_user_roles');
      if (userRoles) {
        const app = localStorage.getItem('_origin_app');
        if (app) {
          userRolePermissionsStore.updateRoles(JSON.parse(userRoles));
          const token = localStorage.getItem('_snap_token');
          if (token) {
            await userRolePermissionsStore.fetchPermissions(app);
          }
        }
      }
    }
    if (route.path === '/') navigateTo('/invoices');
  } else if (
    $auth.value?.verifiedWithPlaid === 'true' ||
    $auth.value?.verifiedWithPlaid === 'verified' ||
    $auth.value?.verifiedWithPlaid === 'suspended' ||
    $auth.value?.verifiedWithPlaid === 'deactivated' ||
    $auth.value?.verifiedWithPlaid === 'unverified'
  ) {
    if (route.path === '/') navigateTo('/pending-payment');
  } else {
    await profileStore.signOut();
  }
});
onMounted(() => {
  if (DefaultLayoutDiv.value) {
    DefaultLayoutDiv.value.$el.addEventListener('click', async (event: MouseEvent) => {
      if (!(event.target instanceof HTMLElement)) {
        return;
      }
      if (
        event.target.nodeName === 'BUTTON' ||
        (event.target.nodeName === 'A' &&
          (event.target.getAttribute('href') === '#' || event.target.getAttribute('href') === 'javascript:void(0)'))
      ) {
        const buttonName =
          event.target.innerText.length > 0 ? event.target.innerText : (event.target.ariaLabel ?? 'Undefined button');
        await $sendButtonClickEvent(buttonName);
      }
    });

    unsubscribeSendEventHook = router.beforeEach((to, from) => {
      const eventData = {
        routeChangeTo: to.path,
        routeChangeFrom: from.path,
      };
      $sendEvent(eventData, EventTypes.AppRouteChanged);
    });
    profileStore.$patch({
      isVerifyPhoneToastVisible: router.currentRoute.value.name !== 'my-preference' && !$auth.value?.phoneVerified,
    });
  }
});

onUnmounted(() => {
  unsubscribeSendEventHook();
});
</script>

<style scoped lang="scss">
.column-flex-100h {
  display: flex;
  flex-direction: column;
  height: 100%;
  flex-grow: 1;
}

.sr__main-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;

  padding: 24px;
}
.help-text {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
  padding: 24px;
  width: 100%;
  color: white;
  font-size: 15px;
  line-height: 20px;
  border-top: 1px solid #292336;
}

@media (max-width: 767px) {
  .help-text {
    flex-direction: column;
    justify-content: center;
  }
}

.sr__not-approved {
  font-size: 24px;
  font-weight: bold;
}

.n-layout .n-layout-content {
  height: calc(100vh - 68px - 99px);
  padding: 0 !important;

  @media (min-width: 768px) {
    height: calc(100vh - 68px - 69px);
  }
}

#default_layout_div .n-layout-content {
  padding: 24px;
}

.welcome {
  margin-bottom: 24px;

  p {
    margin: 0;
  }
}

.row {
  --bs-gutter-x: 0;
}
</style>
